// src/App.js
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import "./App.css";
import LanguageSelect from "./screens/LanguageSelect";
import PaceSelect from "./screens/PaceSelect";
import MainMenu from "./screens/MainMenu";
import Flashcards from "./screens/Flashcards";
import VerbPractice from "./screens/VerbPractice";
import DebugVocab from "./screens/DebugVocab";
import DevDiagnostics from "./screens/DevDiagnostics";
import AuthScreen from "./screens/AuthScreen";
import ProfileSetup from "./screens/ProfileSetup";
import SplashScreen from "./components/SplashScreen";
import { Button, Note } from "./components/ui";
import SentenceCloze from "./screens/SentenceCloze"; // Lückentexte
import PhrasesPractice from "./screens/PhrasesPractice"; // NEU: Redewendungen
import FreeTranslation from "./screens/FreeTranslation"; // Freie Übersetzung
import Friends from "./screens/Friends";
import BlitzDefense from "./screens/BlitzDefense";
import { supabase } from "./supabaseClient";
import { logActivity } from "./lib/activityLog";
import { captureFriendCodeFromUrl, takePendingFriendCode } from "./lib/friends";
import { TARGET_WORDS, PACE_YEARS } from "./data/learningConfig";
import {
  loadProfileSettings,
  saveProfileSettings,
  upsertProfile,
  resetUnlockedWeekRemote,
  loadUserDetails,
} from "./lib/remoteProfiles";

/* ---------- Styles ---------- */
// Seitenhülle der App. Die Screens nutzen die Bausteine aus src/components/ui;
// title bis infoLabel brauchen nur noch die Dev-Screens (DebugVocab, DevDiagnostics).
const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--fluent-fog)",
    color: "var(--fluent-text)",
    // schmaler Rand, damit die Karte am Handy nicht am Bildschirmrand klebt
    padding: "clamp(8px, 3vw, 24px)",
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 720,
    boxSizing: "border-box", // Padding zählt zur Breite → kein Überstand am Handy
    background: "var(--fluent-white)",
    border: "1px solid var(--fluent-border)",
    borderRadius: "var(--fluent-radius-lg)",
    boxShadow: "var(--fluent-shadow-card)",
    padding: "clamp(14px, 4vw, 24px)",
  },
  title: { fontSize: "var(--fluent-font-xl)", fontWeight: 800, margin: 0 },
  subtitle: {
    marginTop: 12,
    marginBottom: 12,
    fontSize: "var(--fluent-font-sm)",
    color: "var(--fluent-text-muted)",
  },
  secondaryButton: {
    padding: "10px 14px",
    borderRadius: "var(--fluent-radius-md)",
    border: "1px solid var(--fluent-border)",
    background: "var(--fluent-white)",
    color: "var(--fluent-text)",
    cursor: "pointer",
    fontSize: "var(--fluent-font-sm)",
  },
  infoBox: {
    marginTop: 8,
    borderRadius: "var(--fluent-radius-md)",
    border: "1px solid var(--fluent-border)",
    padding: 12,
    background: "var(--fluent-fog)",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "var(--fluent-font-sm)",
    marginBottom: 4,
  },
  infoLabel: { color: "var(--fluent-text-muted)", marginRight: 4 },
};

// Bereiche für das Aktivitäts-Protokoll (Dev-Screens werden nicht gezählt)
const ACTIVITY_AREAS = {
  language: "setup",
  pace: "setup",
  main: "menu",
  flashcards: "flashcards",
  verbs: "verbs",
  gaps: "gaps",
  phrases: "phrases",
  translations: "translations",
  friends: "menu", // kein Lernen → zählt nicht für Streak/Lernzeit der Freunde
  blitz: "blitz",
};

// Einladungslink (?friend=CODE) sofort aus der Adresszeile holen und bis nach dem Login merken
captureFriendCodeFromUrl();

/* ---------- App mit Auth + Single-Profile ("default") ---------- */
export default function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Single Profile pro User
  const activeProfileId = "default";

  // Auth-Check: Session + Listener
  useEffect(() => {
    let unsub = null;

    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          setUser(session.user);
        } else {
          setUser(null);
        }
      } catch (e) {
        console.warn("Initial auth check failed:", e);
        setUser(null);
      } finally {
        setAuthChecked(true);
      }

      const { data } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session?.user) {
          setUser(session.user);
        } else {
          setUser(null);
        }
      });

      unsub = () => data.subscription.unsubscribe();
    })();

    return () => {
      if (unsub) unsub();
    };
  }, []);

  // Sprach-/Tempo-Settings (online pro User + default-Profil)
  const [language, setLanguage] = useState(null);
  const [pace, setPace] = useState(null);
  const [view, setView] = useState(
    "language" // "language" | "pace" | "main" | "flashcards" | "verbs" | "gaps" | "phrases" | "translations" | "friends" | "blitz" | "debugVocab" | "devDiag"
  );
  const [weeklyWords, setWeeklyWords] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  // Noch keine persönlichen Angaben (Vorname, Geschlecht, Geburtsdatum) → ProfileSetup zeigen
  const [needsDetails, setNeedsDetails] = useState(false);
  const [activeWeek, setActiveWeek] = useState(1);
  // Code aus einem Einladungslink, mit dem der Freundes-Tab vorausgefüllt öffnet
  const [friendCode, setFriendCode] = useState(null);
  // Angriff, gegen den gerade die Blitzabfrage läuft (und wohin es danach zurückgeht)
  const [blitzAttack, setBlitzAttack] = useState(null);
  const [blitzReturn, setBlitzReturn] = useState("main");
  // Woche, in der zuletzt geübt wurde – das Hauptmenü startet beim Zurückkehren dort
  // (null = noch keine Übung geöffnet → Hauptmenü startet bei der neuesten Woche)
  const [menuWeek, setMenuWeek] = useState(null);

  // Settings aus Supabase laden, wenn User bereit ist
  useEffect(() => {
    if (!user) return;
    setSettingsLoaded(false);

    (async () => {
      // Persönliche Angaben: bei Fehlern (z. B. Tabelle fehlt) überspringen, Login nie blockieren
      try {
        const details = await loadUserDetails(user.id);
        setNeedsDetails(!details);
      } catch (e) {
        console.warn("loadUserDetails failed:", e);
        setNeedsDetails(false);
      }

      try {
        const settings = await loadProfileSettings(user.id, activeProfileId);
        if (settings) {
          setLanguage(settings.languageId);
          setPace(settings.pace);
          setWeeklyWords(settings.weeklyWords);
          setView("main");
        } else {
          // kein Eintrag → Setup von vorne
          setLanguage(null);
          setPace(null);
          setWeeklyWords(null);
          setView("language");
        }
      } catch (e) {
        console.warn("loadProfileSettings failed:", e);
        setView("language");
      } finally {
        setSettingsLoaded(true);
      }
    })();
  }, [user, activeProfileId]);

  // Über einen Einladungslink gekommen → nach dem Laden direkt den Freundes-Tab öffnen
  useEffect(() => {
    if (!user || !settingsLoaded || needsDetails || view !== "main") return;
    const code = takePendingFriendCode();
    if (code) {
      setFriendCode(code);
      setView("friends");
    }
  }, [user, settingsLoaded, needsDetails, view]);

  // Nutzungszeit fürs Admin-Dashboard: beim Öffnen eines Bereichs und bei Eingaben
  // (Klick/Taste), höchstens einmal pro Minute und Bereich (siehe lib/activityLog.js)
  const activityAreaRef = useRef(null);
  useEffect(() => {
    const area = !user || !settingsLoaded ? null : ACTIVITY_AREAS[view] ?? null;
    activityAreaRef.current = area;
    if (area) logActivity(area, language);
  }, [user, settingsLoaded, view, language]);

  useEffect(() => {
    if (!user) return;
    const onInput = () => {
      if (activityAreaRef.current) logActivity(activityAreaRef.current, language);
    };
    window.addEventListener("pointerdown", onInput);
    window.addEventListener("keydown", onInput);
    return () => {
      window.removeEventListener("pointerdown", onInput);
      window.removeEventListener("keydown", onInput);
    };
  }, [user, language]);

  // Startbildschirm: bei jedem Seitenaufruf und direkt nach dem Einloggen.
  // Er überdeckt das Laden von Session und Einstellungen und blendet danach aus.
  const [showSplash, setShowSplash] = useState(true);
  const prevUserRef = useRef(null);

  // Wechsel von „ausgeloggt“ zu „eingeloggt“ → Splash erneut zeigen (vor dem ersten Zeichnen)
  useLayoutEffect(() => {
    if (authChecked && user && !prevUserRef.current) {
      setShowSplash(true);
    }
    prevUserRef.current = user;
  }, [user, authChecked]);

  const splashReady = authChecked && (!user || settingsLoaded);
  const splash = showSplash ? (
    <SplashScreen ready={splashReady} onDone={() => setShowSplash(false)} />
  ) : null;

  // Navigation
  const goToPace = () => {
    if (language) setView("pace");
  };

  const goToMain = async () => {
    if (!pace) return;

    const years = PACE_YEARS[pace] ?? PACE_YEARS.medium;
    const weeks = Math.round(years * 52);
    const ww = weeklyWords ?? Math.ceil(TARGET_WORDS / (weeks || 52));

    setWeeklyWords(ww);

    try {
      if (user) {
        const payload = {
          languageId: language || "es-de",
          pace,
          weeklyWords: ww,
        };

        console.log("[goToMain] upsertProfile + saveProfileSettings", {
          userId: user.id,
          profileId: activeProfileId,
          payload,
        });

        // 1) Profil sicherstellen (FK für profile_settings erfüllen)
        await upsertProfile(user.id, {
          id: activeProfileId, // "default"
          name: "Standard",
        });

        // 2) Einstellungen speichern (vorher alten Stand merken)
        const previous = await loadProfileSettings(user.id, activeProfileId);
        await saveProfileSettings(user.id, activeProfileId, payload);

        // 3) Bei Sprach- oder Tempowechsel gilt die alte Freischaltung nicht mehr
        if (
          previous &&
          (previous.languageId !== payload.languageId ||
            previous.pace !== payload.pace)
        ) {
          try {
            await resetUnlockedWeekRemote(user.id, activeProfileId);
          } catch (e) {
            // z. B. Spalte unlocked_week existiert (noch) nicht
            console.warn("resetUnlockedWeekRemote failed:", e);
          }
        }

        console.log("[goToMain] saveProfileSettings OK");
      } else {
        console.log("[goToMain] no user → cannot save settings");
      }
    } catch (e) {
      console.error("saveProfileSettings failed:", e);
      alert(
        "Fehler beim Speichern der Profileinstellungen: " + (e.message || e)
      );
    }

    setView("main");
  };

  const openFlashcards = (weekly, weekIndex = 1) => {
    setWeeklyWords(weekly || weeklyWords);
    setActiveWeek(weekIndex);
    setMenuWeek(weekIndex);
    setView("flashcards");
  };

  const openVerbPractice = (weekIndex = 1) => {
    setActiveWeek(weekIndex);
    setMenuWeek(weekIndex);
    setView("verbs");
  };

  const openGaps = (weekIndex = 1) => {
    setActiveWeek(weekIndex);
    setMenuWeek(weekIndex);
    setView("gaps");
  };

  const openPhrases = (weekIndex = 1) => {
    setActiveWeek(weekIndex);
    setMenuWeek(weekIndex);
    setView("phrases");
  };

  const openTranslations = (weekIndex = 1) => {
    setActiveWeek(weekIndex);
    setMenuWeek(weekIndex);
    setView("translations");
  };

  const openFriends = () => {
    setFriendCode(null);
    setView("friends");
  };

  const openDefense = (attack, from = "main") => {
    setBlitzAttack(attack);
    setBlitzReturn(from);
    setView("blitz");
  };

  const openDebugVocab = () => setView("debugVocab");
  const openDiagnostics = () => setView("devDiag");

  const backToLanguage = () => setView("language");
  const backToPace = () => {
    setMenuWeek(null); // neue Konfiguration möglich → wieder bei der neuesten Woche starten
    setView("pace");
  };
  const backToMain = () => setView("main");

  // Wird vom Developer-Reset im MainMenu aufgerufen
  const handleHardReset = () => {
    setLanguage(null);
    setPace(null);
    setWeeklyWords(null);
    setMenuWeek(null);
    setView("language");
  };

  const handleForceLogout = async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      window.location.reload();
    }
  };

  if (!authChecked) {
    return (
      <div style={styles.page}>
        {splash}
        <div style={styles.card}>
          <Note>fluent! lädt …</Note>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.page}>
        {splash}
        <div style={styles.card}>
          <AuthScreen onAuthSuccess={setUser} />
        </div>
      </div>
    );
  }

  if (!settingsLoaded) {
    return (
      <div style={styles.page}>
        {splash}
        <div style={styles.card}>
          <Note>fluent! lädt deine Einstellungen …</Note>
          <Button
            variant="secondary"
            size="sm"
            style={{ marginTop: "var(--fluent-space-3)" }}
            onClick={handleForceLogout}
          >
            Logout
          </Button>
        </div>
      </div>
    );
  }

  if (needsDetails) {
    return (
      <div style={styles.page}>
        {splash}
        <div style={styles.card}>
          <ProfileSetup user={user} onDone={() => setNeedsDetails(false)} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {splash}
      <div style={styles.card}>
        {view === "language" && (
          <LanguageSelect
            language={language}
            setLanguage={setLanguage}
            onContinue={goToPace}
          />
        )}

        {view === "pace" && (
          <PaceSelect
            pace={pace}
            setPace={setPace}
            onBack={backToLanguage}
            onContinue={goToMain}
          />
        )}

        {view === "main" && (
          <MainMenu
            language={language}
            pace={pace}
            onBack={backToPace}
            onOpenFlashcards={openFlashcards}
            onOpenVerbs={openVerbPractice}
            onOpenGaps={openGaps}
            onOpenPhrases={openPhrases} // NEU
            onOpenTranslations={openTranslations}
            onOpenFriends={openFriends}
            onOpenDefense={(attack) => openDefense(attack, "main")}
            profileId={activeProfileId}
            onOpenDebugVocab={openDebugVocab}
            onOpenDiagnostics={openDiagnostics}
            userEmail={user.email}
            onLogout={handleForceLogout}
            onHardReset={handleHardReset}
            initialWeek={menuWeek}
          />
        )}

        {view === "flashcards" && (
          <Flashcards
            onBack={backToMain}
            language={language}
            pace={pace}
            weeklyWords={weeklyWords}
            profileId={activeProfileId}
            weekIndex={activeWeek}
          />
        )}

        {view === "verbs" && (
          <VerbPractice
            onBack={backToMain}
            language={language}
            pace={pace}
            profileId={activeProfileId}
            weekIndex={activeWeek}
          />
        )}

        {view === "gaps" && (
          <SentenceCloze
            onBack={backToMain}
            language={language}
            pace={pace}
            profileId={activeProfileId}
            weekIndex={activeWeek}
          />
        )}

        {view === "phrases" && (
          <PhrasesPractice
            onBack={backToMain}
            language={language}
            pace={pace}
            profileId={activeProfileId}
            weekIndex={activeWeek}
          />
        )}

        {view === "translations" && (
          <FreeTranslation
            onBack={backToMain}
            language={language}
            pace={pace}
            profileId={activeProfileId}
            weekIndex={activeWeek}
          />
        )}

        {view === "friends" && (
          <Friends
            language={language}
            pace={pace}
            onBack={backToMain}
            initialCode={friendCode}
            onOpenDefense={(attack) => openDefense(attack, "friends")}
          />
        )}

        {view === "blitz" && blitzAttack && (
          <BlitzDefense
            attack={blitzAttack}
            language={language}
            profileId={activeProfileId}
            backLabel={blitzReturn === "friends" ? "Zurück zu den Freunden" : undefined}
            onBack={() => {
              setBlitzAttack(null);
              setView(blitzReturn);
            }}
          />
        )}

        {view === "debugVocab" && (
          <DebugVocab
            styles={styles}
            onBack={backToMain}
            language={language}
            pace={pace}
            profileId={activeProfileId}
          />
        )}

        {view === "devDiag" && (
          <DevDiagnostics
            styles={styles}
            onBack={backToMain}
            language={language}
            pace={pace}
            profileId={activeProfileId}
          />
        )}
      </div>
    </div>
  );
}
